import moment from 'moment-timezone'
import { fa } from 'vuetify/locale'
import { passwordStrength } from 'check-password-strength'
import * as XLSX from 'xlsx'
import { h } from 'vue'
import { DateTime } from 'luxon'

const memberships = [
  {
    type: 'agencyPrincipal',
    name: 'Agency Principal',
    value: 1500,
    valueRenewal: 1000,
    feePerShow: 0,
    feePerShowDisc: '',
    memberNoDigits: 4
  },
  {
    type: 'agencyMember',
    name: 'Agency Member',
    value: 0,
    valueRenewal: 0,
    feePerShow: 0,
    feePerShowDisc: '',
    memberNoDigits: 4,
    hideFromFrontend: true
  },
  {
    type: 'factoryRep',
    name: 'Factory Rep (W2 Employee)',
    value: 1000,
    valueRenewal: 250,
    feePerShow: 0,
    feePerShowDisc: '',
    memberNoDigits: 4
  },
  {
    type: 'salesRep',
    name: 'Independent Sales Rep (1099 Employee)',
    value: 750,
    valueRenewal: 500,
    feePerShow: 0,
    feePerShowDisc: '',
    memberNoDigits: 4
  },
  {
    type: 'associate',
    name: 'Associate',
    value: 0,
    valueRenewal: 0,
    feePerShow: 100,
    feePerShowDisc: 'Pays $100 fee per show, plus booth costs',
    memberNoDigits: 5
  },
  {
    type: 'manufacturer',
    name: 'Manufacturer',
    value: 1000,
    valueRenewal: 1000,
    feePerShow: 0,
    feePerShowDisc: '',
    memberNoDigits: 5
  },
  {
    type: 'honorary',
    name: 'Honorary',
    value: 0,
    valueRenewal: 0,
    feePerShow: 0,
    feePerShowDisc: '',
    memberNoDigits: 9,
    hideFromFrontend: true
  },

  {
    type: 'inactive',
    name: 'Inactive',
    value: 0,
    valueRenewal: 50,
    feePerShow: 0,
    feePerShowDisc: '',
    memberNoDigits: 9,
    hideFromFrontend: true
  },
  {
    type: 'sponsor',
    name: 'Sponsor',
    value: 0,
    valueRenewal: 0,
    feePerShow: 0,
    feePerShowDisc: '',
    memberNoDigits: 9,
    hideFromFrontend: true
  },
  {
    type: 'unassigned',
    name: 'Unassigned',
    value: 0,
    valueRenewal: 0,
    feePerShow: 0,
    feePerShowDisc: '',
    hideFromFrontend: true
  }
]

export default {
  getMembershipTypes(valuesOnly = false) {
    return valuesOnly
      ? memberships.map(membership => membership.type)
      : memberships
  },
  getMembershipByType(type) {
    return memberships.find(membership => membership.type == type) || false
  },
  translateYearOfOccurrence(input) {
    if (input == 'year-before-season-year')
      return 'Year Before Season Year'
    return 'Season Year'
  },
  translateShowClass(input) {
    if (input == 'indoor-show')
      return 'Indoor Show'
    return 'On-Snow Demo'
  },
  formatDate(input) {
    if (input == null || input === undefined) {
      return ''
    }
    // Handle if input is already in MM-DD-YYYY format
    if (typeof input === 'string' && /^\d{2}-\d{2}-\d{4}$/.test(input)) {
      return input
    }
    return moment.utc(input).tz("America/Denver").format('MM-DD-YYYY')
  },
  formatDateTime(input) {
    if (input == null || input === undefined) {
      return ''
    }
    return moment.utc(input).tz("America/Denver").format('MM-DD-YYYY hh:mm A')
  },
  formatMoney(input) {
    if (input == null || input === undefined) {
      return ''
    }
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    });
    return formatter.format(input);
  },
  formatNumber(input) {
    if (input == null || input === undefined) {
      return ''
    }
    return input.toLocaleString()
  },
  getStates() {
    let states = [
      'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'DC', 'FL', 'GA',
      'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 'MA',
      'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY',
      'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX',
      'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY', 'AS', 'GU', 'MP', 'PR', 'VI'
    ]
    let provinces = [
      'AB', 'BC', 'MB', 'NB', 'NL', 'NS', 'NT', 'NU', 'ON', 'PE', 'QC', 'SK', 'YT'
    ]
    let allStatesAndProvinces = [...states, ...provinces].sort()
    states = allStatesAndProvinces.map(state => {
      return {
        title: state,
        value: state
      }
    })
    states.unshift({
      title: '[Select One]',
      value: null
    })
    return states
  },
  getCarriedLineStates(rawList = false) {
    const states = [
      'Alaska', 'Arkansas', 'Arizona', 'N. California', 'S. California', 'Colorado', 'Hawaii', 'Idaho', 'Kansas', 'Louisiana', 'Missouri', 'Montana', 'Nebraska', 'New Mexico', 'Nevada', 'Oklahoma', 'Oregon', 'South Dakota', 'Texas', 'Utah', 'Washington', 'Wyoming', 'Western United States'
    ]
    if (rawList)
      return states
    return states.map(state => {
      return {
        title: state,
        value: state
      }
    })
  },
  getCarriedLineStatesAbbrevs() {
    const states = [
      'AK', 'AR', 'AZ', 'N. CA', 'S. CA', 'CO', 'HI', 'ID', 'KS', 'LA', 'MO', 'MT', 'NE', 'NM', 'NV', 'OK', 'OR', 'SD', 'TX', 'UT', 'WA', 'WY', 'W. USA'
    ]
    return states.map(state => {
      return {
        title: state,
        value: state
      }
    })
  },
  processInitCollection(sortDictionary) {
    // Deal with sort
    let sortRules = null
    const foundSort = sortDictionary.find(x => x.default)
    if (foundSort) {
      sortRules = {
        sortBy: foundSort.field,
        sortDesc: foundSort.default == 'desc' ? true : false
      }
    } else {
      sortRules = {
        sortBy: '_id',
        sortDesc: false
      }
    }

    return {
      totalPages: 0,
      totalCount: 0,
      records: [],
      pageNo: 1,
      sortBy: sortRules.sortBy,
      sortDesc: sortRules.sortDesc
    }
  },
  processUpdateSortBy(input, sortDictionary) {
    if (input.length) {
      return {
        sortBy: input[0].key,
        sortDesc: input[0].order == 'desc' ? true : false
      }
    } else {
      const getDefaults = this.processInitCollection(sortDictionary)
      return {
        sortBy: getDefaults.sortBy,
        sortDesc: getDefaults.sortDesc
      }
    }
  },
  getIsSortableCol(colName, sortDictionary) {
    return sortDictionary.find(x => x.field == colName) ? true : false
  },
  getSearchFilterOptions(filterDictionary, suppress) {
    let options = []
    filterDictionary.forEach(x => {
      if (!suppress.includes(x.field))
        options.push({
          title: x.text,
          value: x
        })
    })
    return options
  },
  displayCond(element) {
    return element || 'N/A'
  },
  transformToArray(object) {
    let toReturn = []
    Object.entries(object).forEach(x => {
      toReturn.push(x[1])
    })
    return toReturn
  },
  getPasswordStatus(password, confirmPassword, canBeBlank = false ) {
    if (!password && !confirmPassword && canBeBlank) {
      return {
        color: 'blue-grey',
        message: 'Enter Password and Confirm Password, or leave blank to keep unchanged'
      }
    }
    else if (!password && !confirmPassword) {
      return {
        color: 'blue-grey',
        message: 'Enter Password and Confirm Password'
      }
    }
    else if (password != confirmPassword) {
      return {
        color: 'red',
        message: 'Passwords do not match'
      }
    }
    else if (password.length < 6) {
      return {
        color: 'red',
        message: 'Password must be at least 6 characters'
      }
    }
    else {
      const currPasswordStrength = passwordStrength(password).id
      if (currPasswordStrength == 0) {
        return {
          color: 'red',
          message: 'Password strength is too weak. Please supply a different Password'
        }
      } else if (currPasswordStrength == 1) {
        return {
          color: 'red',
          message: 'Password strength is weak'
        }
      } else if (currPasswordStrength >= 2) {
        return {
          color: 'green',
          message: 'Password strength is strong'
        }
      }
    }
  },
  displayAddress(address) {
    let finalAddress = address.streetAddress1 + '<br>'
    if (address.streetAddress2)
      finalAddress += address.streetAddress2 + '<br>'
    finalAddress += address.city + ', ' + address.state + ' ' + address.postcode
    return finalAddress
  },
  displayShowsAttending(item) {
    let finalList = ''
    item.attendeeSignups.forEach(x => {
      finalList += `<li><a class="link-table link-table--no-margin" href="${process.env.WP_BASE_URL}shows/${x.showSlug}" target="_blank">${x.showName}</li>`
    })
    return finalList
  },
  displayShowsExhibiting(item) {
    let finalList = ''
    item.exhibitorSignups.forEach(x => {
      finalList += `<li><a class="link-table link-table--no-margin" href="${process.env.WP_BASE_URL}shows/${x.showSlug}" target="_blank">${x.showName}</li>`
    })
    return finalList
  },
  displayRegions(regions) {
    return regions.join(', ')
  },
  displayCarriedLines(carriedLines) {
    const carriedLineStates = this.getCarriedLineStates(),
      carriedLineStatesAbbrevs = this.getCarriedLineStatesAbbrevs()
    let finalList = '',
      statesList = []
    carriedLines.forEach(x => {
      finalList += `<li>`
      finalList += x.brandName + ' ('
      x.states.forEach(y => {
        const foundIndex = carriedLineStates.findIndex(z => z.title == y)
        if (foundIndex > -1) {
          statesList.push(carriedLineStatesAbbrevs[foundIndex].title)
        }
      })
      finalList += statesList.join(', ')
      finalList += ')</li>'
    })
    return finalList
  },
  deepClone(elem) {
    return JSON.parse(JSON.stringify(elem))
  },
  generateRandomId() {
    const timestampPart = Date.now().toString().slice(-5); // last 5 digits of timestamp
    const randomPart = Math.floor(Math.random() * 900) + 100; // 3-digit random number
    return timestampPart + randomPart; // 5 + 3 = 8 digits
  },
  displayMemberNo(memberNo) {
    if(/^\d{10}$/.test(String(memberNo))) {
      return '—'
    }
    return memberNo
  },
  getValueByPath(obj, path) {
    return path.split('.').reduce((acc, part) => acc?.[part], obj)
  },
  powerDataGetSearchFilters(filters) {
  return (filters || [])
    .filter(f => f && f.key) // ensure it's not null/undefined and has key
    .reduce((acc, { key, title, options }) => {
      acc[key] = {
        title,
        search: '',
        ...(options && { options })
      }
      return acc
    }, {})
  },
  powerDataFilterSubRecordsInner(records, filters, omit = false) {
    if (!records || !Array.isArray(records) || Object.keys(filters || {}).length === 0) {
      return records || [];
    }

    const getValue = (obj, path) => {
      return path.split('.').reduce((o, key) => (o && o[key] != null ? o[key] : null), obj)
    }

    return records.filter(record => {
      return Object.entries(filters).every(([key, search]) => {
        if (!search) return true;

        const searchStr = String(search).trim().toLowerCase();
        const targetStr = String(getValue(record, key) ?? "").toLowerCase(); // <-- FIX HERE

        if (!searchStr) return true;

        let terms, mustAll = false;
        if (searchStr.includes("+")) {
          terms = searchStr.split("+").map(s => s.trim()).filter(Boolean);
          mustAll = true;
        } else if (searchStr.includes(",")) {
          terms = searchStr.split(",").map(s => s.trim()).filter(Boolean);
        } else {
          terms = [searchStr];
        }

        const matched = mustAll
          ? terms.every(term => targetStr.includes(term))
          : terms.some(term => targetStr.includes(term));

        return omit ? !matched : matched;
      });
    });
  },
  powerDataFilterAndSortSubRecords(records, include = {}, omit = {}, sort = {}) {
    const hasIncludes = Object.keys(include).length > 0
    const hasOmits = Object.keys(omit).length > 0
    const hasSort = Object.keys(sort).length > 0
    if (!hasIncludes && !hasOmits && !hasSort) return records

    const buildLooseRegex = value => new RegExp(value.split('').join('.*'), 'i')

    const getValue = (obj, path) => {
      return path.split('.').reduce((o, key) => (o && o[key] != null ? o[key] : null), obj)
    }

    // Init result
    let result = records
    // Filter for includes
    result = this.powerDataFilterSubRecordsInner(result, include)
    // Filter for omits
    result = this.powerDataFilterSubRecordsInner(result, omit, true)


    if (hasSort) {
      result.sort((a, b) => {
        for (const [key, direction] of Object.entries(sort)) {
          const valA = getValue(a, key)
          const valB = getValue(b, key)

          if (valA == null && valB == null) continue
          if (valA == null) return direction === 1 ? 1 : -1
          if (valB == null) return direction === 1 ? -1 : 1

          if (valA < valB) return direction === 1 ? -1 : 1
          if (valA > valB) return direction === 1 ? 1 : -1
        }
        return 0
      })
    }

    return result
  },
  exportToXlsx(columns, records, filename) {
    const getNested = (obj, path) => {
      if (!path || typeof path !== 'string') return undefined;
      return path.split('.').reduce((acc, part) => (acc && acc[part] !== undefined ? acc[part] : undefined), obj);
    };

    const data = [columns.map(col => col.title)];
    const dateCells = [];

    const excelDateSerial = date => (Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) - Date.UTC(1899, 11, 30)) / 86400000;

    for (let r = 0; r < records.length; r++) {
      const rec = records[r];
      const row = [];

      for (let c = 0; c < columns.length; c++) {
        const col = columns[c];
        const rawVal = getNested(rec, col.key);

        if (col.autoFormat === 'date') {
          const date = rawVal ? new Date(rawVal) : null;

          if (date instanceof Date && !isNaN(date)) {
            const serial = excelDateSerial(date);
            row.push(serial);
            dateCells.push({ r: r + 1, c }); // +1 to account for header
            continue;
          } else {
            row.push('');
            continue;
          }
        }

        row.push(rawVal ?? '');
      }

      data.push(row);
    }

    const ws = XLSX.utils.aoa_to_sheet(data);

    // Apply date formatting properly: number + format string
    for (const { r, c } of dateCells) {
      const cellRef = XLSX.utils.encode_cell({ r, c });
      if (ws[cellRef]) {
        ws[cellRef].t = 'n'; // number
        ws[cellRef].z = 'yyyy-mm-dd'; // Excel date format
      }
    }

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');

    const wbOut = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([wbOut], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });

    const url = URL.createObjectURL(blob);
    const link = Object.assign(document.createElement('a'), {
      href: url,
      download: `${filename}.xlsx`,
      style: 'display:none'
    });
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  },
  getSubmissionSignup(signup, exhibitorsOrAttendees, shows) {
    // Find the show that matches
    const matchingShow = shows.find(x => x._id == signup.showId)
    if (!matchingShow)
      return []

    // Assemble final object and index
    let finalObj = [],
      index = 0

    // Get all the normal fields
    let normalFieldPrereqs = []
    if (exhibitorsOrAttendees == 'Attendees') {
      normalFieldPrereqs = []
    } else if (exhibitorsOrAttendees == 'Exhibitors') {
      normalFieldPrereqs = [
        "Number of Booths",
        "Sharing With",
        "Same as Last Year",
        "Special Drape Configurations",
        "Move-Out Day",
        "Vehicle Description",
        "Exhibition Categories",
        "Carried Lines Bringing",
        "Payment Type",
      ]
    }

    normalFieldPrereqs.forEach(x => {
      // Only add if field is present in the signup
      const fieldValue = signup.customFieldAnswers[x]
      if (fieldValue !== undefined && (x != 'Move-Out Day' || (x == 'Move-Out Day' && fieldValue != 'N/A'))) {
        finalObj.push({
          field: x,
          label: x,
          value: fieldValue,
          index: index
        })
        index++
      }
    })

    // Get all the custom fields that are for attendees
    const customFields = matchingShow.customFields ? matchingShow.customFields.filter(x => x.accountType == exhibitorsOrAttendees) : []
    // For each custom field
    customFields.forEach(x => {
      // Find the signup.customFields value that matches
      const value = signup.customFields && signup.customFields[x.field] != undefined
        ? signup.customFields[x.field]
        : 'Not Answered'
      // And then add it to the final object
      finalObj.push({
        field: x.field,
        label: x.label,
        value: value,
        index: index
      })
      index++
    })

    // And return final object
    return finalObj
  },
  convertUtcToMstDateTime(utcString) {
    // Parse ISO string as UTC, then shift to local midnight
    const localMidnight = DateTime.fromISO(utcString, { zone: 'utc' })
      .setZone('local')
      .startOf('day')

    return localMidnight.toJSDate()
  },
  createBottomOfPageHandler(action, tolerance = 5) {
    let busy = false

    return async function onScroll() {
      const scrollPosition = window.scrollY + window.innerHeight
      const bottomPosition = document.documentElement.scrollHeight

      if (scrollPosition >= bottomPosition - tolerance) {
        if (busy) return
        busy = true
        try {
          await action()
        } finally {
          busy = false
        }
      }
    }
  },
  getPaymentModalSchema(savedPaymentMethods, defaultSelectedPaymentMethod, customerProfileId) {
    return {
      active: false,
      amount: null,
      balance: null,
      description: null,
      savedPaymentMethods: savedPaymentMethods || [],
      selectedPaymentMethod: defaultSelectedPaymentMethod || null,
      customerProfileId: customerProfileId || null,
      cardholderFirstName: null,
      cardholderLastName: null,
      cardNumber: null,
      month: null,
      year: null,
      cardCode: null,
      opaqueData: null,
      address: {
        streetAddress1: null,
        streetAddress2: null,
        city: null,
        state: null,
        postcode: null
      }
    }
  },
  scrollToTop(elementId) {
    setTimeout(function () {
      document.getElementById(elementId).scrollTo({top: 0, behavior: 'smooth'})
    }, 2)
  },
  async downloadExcel(response, filename = 'export.xlsx') {
    if (!response.ok) {
      throw new Error(`Failed to download file: ${response.statusText}`)
    }

    const blob = await response.blob()
    const url = window.URL.createObjectURL(blob)

    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    a.remove()

    window.URL.revokeObjectURL(url)
  },
  getLiabilityFormStatusBgColor(value) {
    const green = '#43A047',
      amber = '#FFC107'
    if (value == true) {
      return green
    } else {
      return amber
    }
  },
  buildStrictFilters(filters, isArray = false, omit = false) {
    if (!filters) return false

    if (isArray) {
      // merge all array-field conditions into one $elemMatch
      const elemMatch = {}

      for (const [key, value] of Object.entries(filters)) {
        if (!value || !value.search) continue

        const search = value.search.trim()
        if (!search) continue

        elemMatch[key] = { $regex: search, $options: "i" }
      }

      if (Object.keys(elemMatch).length === 0) return false

      return { $elemMatch: elemMatch }
    }

    // scalar fields
    const clauses = []
    for (const [key, value] of Object.entries(filters)) {
      if (!value || !value.search) continue

      const search = value.search.trim()
      if (!search) continue

      clauses.push({
        $expr: {
          $regexMatch: {
            input: { $toString: `$${key}` },
            regex: search,
            options: "i"
          }
        }
      })
    }

    if (!clauses.length) return false
    return clauses.length === 1 ? clauses[0] : { $and: clauses }
  },
  getItemsForPowerDataTable(items, header, searchMode) {
    if (items && items.length > 0)
      return items
    if (!header || !Array.isArray(header)) {
      return []
    }
    let objToReturn = {
      index: 0
    }
    header.forEach(x => {
      objToReturn[x.key] = ''
    })
    return [objToReturn]
  },
  calculateLedgerTotal(ledger) {
    return ledger.reduce((total, item) => {
      if (item.type == 'Charge' || item.type == 'Offline Charge' || item.type == 'Refund') {
        return total + parseFloat(item.amount)
      }
      return total
    }, 0)
  },
  calculateAttendeeLineItems(show, customFields, attendees) {
    if (!show)
      return []

    const showCustomFields = show?.customFields?.filter(x => x.priceType == 'Has Price Value');

    // Custom Fields may be undefined for a Signup if the field was added AFTER signups were created
    let cfLineItems = []
    if (customFields && showCustomFields) {
      cfLineItems = showCustomFields.map(x => {
        return {
          label: x.label,
          paymentType: '',
          value: parseFloat(customFields[x.field]) || 0
        }
      })
    }

    const attendeeLineItems = attendees.filter(a => !a.deleted).map(a => {
      return {
        label: a.name?.first + ' ' + a.name?.last,
        paymentType: a.paymentType,
        value: a.paymentAmount || 0
      }
    }).sort((a, b) => a.label.localeCompare(b.label))

    return [...attendeeLineItems, ...cfLineItems]
  },
  calculateAttendeeLineItemsTotal(show, customFields, attendees) {
    if (!show)
      return 0

    const lineItems = this.calculateAttendeeLineItems(show, customFields, attendees)
    return lineItems.reduce((total, item) => {
      return total + parseFloat(item.value)
    }, 0)
  }
}
